import { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import toast from 'react-hot-toast';
import jobServices from 'ApiService/Student/jobServices';

const StudentOffers = () => {
  const { state } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [jobOffers, setJobOffers] = useState([]);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  const getTabCount = (tab) => {
    if (tab === 'all') return jobOffers.length;
    if (tab === 'pending')
      return jobOffers.filter((offer) => offer.studentRevert === 'Pending')
        .length;
    if (tab === 'accepted')
      return jobOffers.filter((offer) => offer.studentRevert === 'Accepted')
        .length;
    if (tab === 'declined')
      return jobOffers.filter(
        (offer) =>
          offer.studentRevert === 'Declined' ||
          offer.studentRevert === 'Auto Declined'
      ).length;
    return 0;
  };

  const filteredOffers = jobOffers.filter((offer) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return offer.studentRevert === 'Pending';
    if (activeTab === 'accepted') return offer.studentRevert === 'Accepted';
    if (activeTab === 'declined')
      return (
        offer.studentRevert === 'Declined' ||
        offer.studentRevert === 'Auto Declined'
      );
    return true;
  });

  const handleViewOfferLetter = (e, filePath) => {
    e.stopPropagation();
    if (!filePath) return;

    try {
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL.replace(
        '/api/v1',
        ''
      )}/${filePath.replace(/\\/g, '/')}`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      toast.error('Failed to open PDF');
    }
  };

  const handlePreviewOfferLetter = (e, filePath) => {
    e.stopPropagation();
    if (!filePath) return;

    try {
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL.replace(
        '/api/v1',
        ''
      )}/${filePath.replace(/\\/g, '/')}`;
      setPreviewPdf(pdfUrl);
    } catch (error) {
      toast.error('Failed to preview PDF');
    }
  };

  const fetchStudentOffers = async () => {
    setLoading(true);
    try {
      const response = await jobServices.getStudentOfferDetails();

      if (response.data.success) {
        setJobOffers(response.data.data || []);
      } else {
        toast.error(response.data.message || 'Failed to fetch job offers');
        setJobOffers([]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch job offers'
      );
      setJobOffers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudentOffers();
  }, []);

  return (
    <div className="w-full p-4 sm:overflow-auto">
      <div className="p-4">
        <h1 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">
          My Job Offers
        </h1>

        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="mt-4">
            {/* Tabs */}
            <div className="mb-6 flex gap-6 border-b border-gray-200 dark:border-white/10">
              {['all', 'pending', 'accepted', 'declined'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-3 text-sm font-medium capitalize transition-colors ${
                    activeTab === tab
                      ? 'border-b-2 border-brand-500 text-brand-500'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {tab} ({getTabCount(tab)})
                </button>
              ))}
            </div>

            {filteredOffers.length === 0 ? (
              <div className="rounded-lg bg-white p-6 text-center shadow-md dark:bg-navy-800">
                <p className="text-gray-600 dark:text-gray-300">
                  No job offers found.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredOffers.map((offer) => (
                  <div
                    key={offer.assignmentId}
                    className="overflow-hidden rounded-lg bg-white shadow-md transition-all hover:shadow-lg dark:bg-navy-800"
                  >
                    <div className="p-4">
                      <div className="mb-4 flex items-center">
                        {offer.job.companyId?.logo ? (
                          <img
                            src={`${process.env.REACT_APP_API_BASE_URL.replace(
                              '/api/v1',
                              ''
                            )}/${offer.job.companyId.logo.replace(/\\/g, '/')}`}
                            alt={offer.job.companyId?.name || 'Company Logo'}
                            className="mr-3 h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-500 dark:bg-blue-900">
                            <span className="text-lg font-bold">
                              {(offer.job.companyName || 'C').charAt(0)}
                            </span>
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-gray-800 dark:text-white">
                            {offer.job.companyId?.name ||
                              offer.job.companyName ||
                              'Company'}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {offer.job.title || 'Job Title'}
                          </p>
                        </div>
                      </div>

                      {/* Offer Details */}
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Salary:
                          </span>
                          <span className="font-medium text-gray-800 dark:text-white">
                            {offer.offerDetails?.salary
                              ? `₹${offer.offerDetails.salary.toLocaleString()}`
                              : 'Not specified'}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Location:
                          </span>
                          <span className="font-medium text-gray-800 dark:text-white">
                            {offer.offerDetails?.location || 'Not specified'}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Joining Date:
                          </span>
                          <span className="font-medium text-gray-800 dark:text-white">
                            {offer.offerDetails?.joiningDate
                              ? new Date(
                                  offer.offerDetails.joiningDate
                                ).toLocaleDateString()
                              : 'Not specified'}
                          </span>
                        </div>

                        {offer.offerDetails?.bondDuration && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              Bond Duration:
                            </span>
                            <span className="font-medium text-gray-800 dark:text-white">
                              {`${offer.offerDetails.bondDuration} months`}
                            </span>
                          </div>
                        )}

                        {offer.offerDetails?.benefits?.length > 0 && (
                          <div>
                            <span className="block text-sm text-gray-500 dark:text-gray-400">
                              Benefits:
                            </span>
                            <ul className="list-inside list-disc text-gray-800 dark:text-white">
                              {offer.offerDetails.benefits.map(
                                (benefit, index) => (
                                  <li key={index}>{benefit}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}

                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Status:
                          </span>
                          <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
                            {offer.status}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Your Response:
                          </span>
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${
                              offer.studentRevert === 'Accepted'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : offer.studentRevert === 'Rejected'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            }`}
                          >
                            {offer.studentRevert}
                          </span>
                        </div>
                      </div>

                      {offer.offerLetterAvailable &&
                        offer.offerDetails?.offerLetterFilePath && (
                          <div className="mt-4 flex space-x-2">
                            <button
                              onClick={(e) =>
                                handleViewOfferLetter(
                                  e,
                                  offer.offerDetails.offerLetterFilePath
                                )
                              }
                              className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                              View Offer Letter
                            </button>
                            <button
                              onClick={(e) =>
                                handlePreviewOfferLetter(
                                  e,
                                  offer.offerDetails.offerLetterFilePath
                                )
                              }
                              className="rounded-md bg-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                            >
                              Preview
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PDF Preview Modal */}
        {previewPdf && (
          <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
            <div className="relative w-full max-w-4xl rounded-lg bg-white p-4 dark:bg-gray-800">
              <button
                onClick={() => setPreviewPdf(null)}
                className="absolute right-2 top-2 rounded-full bg-gray-200 p-1 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Offer Letter Preview
              </h3>
              <iframe
                src={`${previewPdf}#toolbar=1`}
                width="100%"
                height="500"
                className="rounded border border-gray-300"
                title="Offer Letter PDF"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentOffers;
